"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Search } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { filterArticles, type SearchableCollection } from "@/lib/help-search";

export function HelpSearch({ collections }: { collections: SearchableCollection[] }) {
  const t = useTranslations("Klantenservice");
  const [query, setQuery] = useState("");

  const searching = query.trim() !== "";
  const results = useMemo(() => filterArticles(collections, query), [collections, query]);

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("searchPlaceholder")}
          aria-label={t("searchPlaceholder")}
          className="w-full rounded-xl border border-line bg-white py-3.5 pl-12 pr-4 text-[15px] text-navy outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
        />
      </div>

      {searching ? (
        results.length === 0 ? (
          <p className="mt-8 text-[15px] text-slate">{t("searchEmpty", { query })}</p>
        ) : (
          <ul className="mt-8 divide-y divide-line">
            {results.map((article) => (
              <li key={article.slug}>
                <Link
                  href={`/klantenservice/${article.slug}`}
                  className="block py-4 transition hover:opacity-80"
                >
                  <span className="block text-[15px] font-semibold text-navy">
                    {article.title}
                  </span>
                  <span className="mt-1 block text-sm text-slate">{article.excerpt}</span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {collections.map((collection) => (
            <div key={collection.slug} className="rounded-[1.5rem] bg-mist p-6">
              <h2 className="text-[17px] font-extrabold text-navy">
                <Link
                  href={`/klantenservice/${collection.slug}`}
                  className="transition-colors hover:text-brand"
                >
                  {collection.title}
                </Link>
              </h2>
              {collection.description ? (
                <p className="mt-1 text-sm text-muted">{collection.description}</p>
              ) : null}
              <ul className="mt-4 space-y-2.5">
                {collection.articles.map((article) => (
                  <li key={article.slug}>
                    <Link
                      href={`/klantenservice/${article.slug}`}
                      className="text-sm text-slate transition-colors hover:text-navy"
                    >
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
